// src/homePage.jsx
import React, { useEffect, useState } from "react";
import { calculateEmiOptions, computeApprovedCreditLimit } from "./App.jsx";
import { usePDF } from "react-to-pdf";

const RETAILERS_API_URL = "https://usethecred.com/api/v1retailer.php";
const CREATE_LOAN_API_URL = "https://usethecred.com/api/v1create_loan.php";

/* ------------ Toast (popup error) ------------ */
function Toast({ message }) {
  if (!message) return null;

  return (
    <div className="toast">
      <span className="toast-text">{message}</span>
    </div>
  );
}

/* ------------ Home page ------------ */
export function HomePage() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); 

  useEffect(() => {
    async function fetchRetailers() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(RETAILERS_API_URL);
        const json = await res.json();

        if (!res.ok || !json.success) {
          throw new Error(json.message || "Failed to fetch retailers");
        }

        setRetailers(json.data || []);
      } catch (err) {
        console.error(err);
        setError("Unable to load retailers.");
      } finally {
        setLoading(false);
      }
    }

    fetchRetailers();
  }, []);

  const handleCardClick = (retailer) => {
    setSelectedRetailer(retailer);
  };

  const handleCloseForm = () => {
    setSelectedRetailer(null);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleRetailers = normalizedSearch
  ? retailers.filter((r) =>
      (r.retailer_name || "")
        .toLowerCase()
        .includes(normalizedSearch)
    )
  : retailers;


  return (
    <>
      <div className="page page-home">
        <h2 className="page-title">Retailers</h2>
        <div className="form-group">
          <input
            type="text"
            className="field-input"
            placeholder="Search retailer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading && <p className="text-muted">Loading retailers...</p>}
        {error && <p className="text-error">{error}</p>}


        {!loading && !error && visibleRetailers.length === 0 && (
          <p className="text-muted">No retailers found.</p>
        )}

      <div className="card-list">
        {visibleRetailers.map((retailer) => (
          <button
            key={retailer.id}
            className="card retailer-card retailer-card-button"
            onClick={() => handleCardClick(retailer)}
          >
            <div className="card-main">
              <div className="card-title">{retailer.retailer_name}</div>
            </div>
          </button>
        ))}
      </div>
      </div>

      {selectedRetailer && (
        <CustomerFormSheet retailer={selectedRetailer} onClose={handleCloseForm} />
      )}
    </>
  );
}

/* ---------------- Multi-step sheet + steps ---------------- */
const TOTAL_STEPS = 5;

function CustomerFormSheet({ retailer, onClose }) {
  const [step, setStep] = React.useState(1);
  const [selectedPlan, setSelectedPlan] = React.useState(null);
  const [error, setError] = React.useState("");
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const [createdLoanId, setCreatedLoanId] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [form, setForm] = React.useState({
    // Step 1 - Customer
    customerPhoto: null,
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerDob: "",
    customerMonthlySalary: "",
    customerAddress: "",
    // Step 2 - KYC
    aadharNumber: "",
    aadharFront: null,
    aadharBack: null,
    panNumber: "",
    panPhoto: null,
    accountNumber: "",
    ifscCode: "",
    // Step 3 - Item
    itemName: "",
    itemValue: "",
    itemImei: "",
    itemPhoto: null,
    // Step 4 - Downpayment & EMI + Eligibility extras
    downPayment: "",
    cibilScore: "",
    currentEmi: "",
    deviceType: "ANDROID", // ANDROID / IOS
  });

  // Lock body scroll while sheet open
  React.useEffect(() => {
    document.body.classList.add("body-locked");
    return () => {
      document.body.classList.remove("body-locked");
    };
  }, []);

  // Auto-hide error toast after 3 seconds
  React.useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleTextChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      let newValue = value;

      if (name === "aadharNumber") {
        newValue = value.replace(/\D/g, "").slice(0, 12);
      }

      if (name === "panNumber") {
        newValue = value.toUpperCase();
      }

      if (name === "ifscCode") {
        newValue = value.toUpperCase();
      }

      if (name === "cibilScore") {
        newValue = value.replace(/\D/g, "").slice(0, 3);
      }

      const next = { ...prev, [name]: newValue };

      // Auto-fill downpayment based on item value
      if (name === "itemValue") {
        const num = Number(newValue) || 0;
        let autoDp = 0;
        if (num >= 10000 && num <= 20000) {
          autoDp = Math.ceil(num * 0.3);
        } else if (num >= 20001 && num <= 35000) {
          autoDp = Math.ceil(num * 0.3);
        } else if (num >= 35001 && num <= 50000) {
          autoDp = Math.ceil(num * 0.25);
        } else if (num >= 50001) {
          autoDp = Math.ceil(num * 0.2);
        }
        next.downPayment = autoDp ? String(autoDp) : "";
      }

      return next;
    });
  };

  const handleDeviceTypeChange = (type) => {
    setForm((prev) => ({ ...prev, deviceType: type }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files && files[0] ? files[0] : null;
    setForm((prev) => ({ ...prev, [name]: file }));
  };

  const progressPercent = (step / TOTAL_STEPS) * 100;

  // ---- Validations ----
  const validateStep1 = () => {
    if (!form.customerName.trim()) return "Customer name is required.";
    if (!form.customerPhone.trim()) return "Phone number is required.";
    if (!form.customerDob) return "Date of birth is required.";
    if (!form.customerMonthlySalary) return "Monthly salary is required.";
    if (!form.customerAddress.trim()) return "Address is required.";

    const today = new Date();
    const dob = new Date(form.customerDob);
    if (isNaN(dob.getTime())) {
      return "Enter a valid date of birth.";
    }

    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 21 || age > 50) {
      return "Customer age must be between 21 and 50 years.";
    }

    return "";
  };

  const validateStep2 = () => {
    if (!form.aadharNumber.trim()) return "Aadhaar number is required.";
    if (!/^\d{12}$/.test(form.aadharNumber)) {
      return "Aadhaar number must be exactly 12 digits.";
    }
    if (!form.panNumber.trim()) return "PAN number is required.";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber)) {
      return "Enter a valid PAN (5 letters, 4 digits, 1 letter, all caps).";
    }
    if (!form.accountNumber.trim()) return "Account number is required.";
    if (!form.ifscCode.trim()) return "IFSC code is required.";
    return "";
  };

  const validateStep3 = () => {
    if (!form.itemName.trim()) return "Item name is required.";
    if (!form.itemValue || Number(form.itemValue) <= 0)
      return "Item value must be greater than 0.";
    return "";
  };

  const validateStep4 = () => {
    const eligibility = computeApprovedCreditLimit({
      itemValue: form.itemValue,
      downPayment: form.downPayment,
      income: form.customerMonthlySalary,
      cibil: form.cibilScore,
      existingEmi: form.currentEmi,
    });

    if (!eligibility.ok) {
      return eligibility.error;
    }

    if (!form.cibilScore) {
      return "CIBIL score is required.";
    }

    if (!selectedPlan) {
      return "Select an EMI plan to continue.";
    }

    return "";
  };

  const handleNext = () => {
    if (createdLoanId) return;

    let msg = "";
    if (step === 1) msg = validateStep1();
    if (step === 2) msg = validateStep2();
    if (step === 3) msg = validateStep3();
    if (step === 4) msg = validateStep4();

    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const handleBack = () => {
    if (createdLoanId) return;
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  // ---- EMI options for Step 4 ----
  const itemValueNum = Number(form.itemValue) || 0;
  const downPaymentNum = Number(form.downPayment) || 0;

  const eligibilityForEmi =
    itemValueNum > 0 && downPaymentNum > 0
      ? computeApprovedCreditLimit({
          itemValue: form.itemValue,
          downPayment: form.downPayment,
          income: form.customerMonthlySalary,
          cibil: form.cibilScore,
          existingEmi: form.currentEmi,
        })
      : { ok: false };

  const emiOptions =
    step >= 4 &&
    itemValueNum > 0 &&
    downPaymentNum > 0 &&
    eligibilityForEmi.ok
      ? calculateEmiOptions(
          eligibilityForEmi.creditLimit + downPaymentNum,
          downPaymentNum,
          form.deviceType || "ANDROID"
        )
      : [];

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    if (createdLoanId) return;

    const step4Error = validateStep4();
    if (step4Error) {
      setError(step4Error);
      return;
    }
    if (!isConfirmed) {
      setError("You must accept the loan terms to create the loan.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      const payload = { retailer, form, selectedPlan, emiOptions };

      const res = await fetch(CREATE_LOAN_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      console.log("Create loan raw response:", res.status, text);

      let json;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error("Invalid JSON from server");
      }

      if (!res.ok || !json.success) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }

      setCreatedLoanId(json.loan_id);
    } catch (err) {
      console.error("Create loan error:", err);
      setError("Unable to create loan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sheet-backdrop">
      <div className="sheet-container">
        <header className="sheet-header">
          <button
            className="sheet-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ✕
          </button>
          <div className="sheet-header-text">
            <div className="sheet-title">
              {createdLoanId ? "Loan Created" : "New Loan Application"}
            </div>
            <div className="sheet-subtitle">
              Retailer: {retailer.retailer_name}
            </div>
          </div>
        </header>

        {!createdLoanId && (
          <>
            <div className="sheet-progress-wrap">
              <div className="step-progress-bar">
                <div
                  className="step-progress-bar-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="step-progress-text">
                Step {step} of {TOTAL_STEPS}
              </div>
            </div>

            <div className="sheet-body">
              {step === 1 && (
                <StepCustomerDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 2 && (
                <StepKycDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 3 && (
                <StepItemDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 4 && (
                <StepDownpaymentEmi
                  form={form}
                  onTextChange={handleTextChange}
                  emiOptions={emiOptions}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  onDeviceTypeChange={handleDeviceTypeChange}
                />
              )}

              {step === 5 && (
                <StepPreview
                  retailer={retailer}
                  form={form}
                  selectedPlan={selectedPlan}
                  isConfirmed={isConfirmed}
                  setIsConfirmed={setIsConfirmed}
                />
              )}
            </div>

            <div className="sheet-footer sheet-footer--wizard">
              <button
                type="button"
                className="btn btn-secondary sheet-footer-btn"
                onClick={step === 1 ? onClose : handleBack}
                disabled={isSubmitting}
              >
                {step === 1 ? "Close" : "Back"}
              </button>
              {step < TOTAL_STEPS && (
                <button
                  type="button"
                  className="btn btn-primary sheet-footer-btn"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </button>
              )}
              {step === TOTAL_STEPS && (
                <button
                  type="submit"
                  className="btn btn-primary sheet-footer-btn"
                  onClick={handleCreateLoan}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Loan"}
                </button>
              )}
            </div>
          </>
        )}

        {createdLoanId && (
          <SuccessView
            loanId={createdLoanId}
            onClose={onClose}
            retailer={retailer}
            form={form}
            selectedPlan={selectedPlan}
          />
        )}

        <Toast message={error} />
      </div>
    </div>
  );
}

/* ---- Step 1: Customer details ---- */
function StepCustomerDetails({ form, onTextChange, onFileChange }) {
  return (
    <section className="section-block">
      <h3 className="section-title">Customer Details</h3>

      <div className="form-group">
        <label className="field-label">Customer Name</label>
        <input
          type="text"
          name="customerName"
          className="field-input"
          placeholder="Enter full name"
          value={form.customerName}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Mobile Number</label>
        <input
          type="tel"
          name="customerPhone"
          className="field-input"
          placeholder="10-digit mobile number"
          value={form.customerPhone}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Email (optional)</label>
        <input
          type="email"
          name="customerEmail"
          className="field-input"
          placeholder="name@example.com"
          value={form.customerEmail}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Date of Birth</label>
        <input
          type="date"
          name="customerDob"
          className="field-input"
          value={form.customerDob}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Monthly Salary</label>
        <input
          type="number"
          name="customerMonthlySalary"
          className="field-input"
          placeholder="Net monthly income"
          value={form.customerMonthlySalary}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Current Address</label>
        <textarea
          name="customerAddress"
          className="field-input"
          rows={3}
          placeholder="House / Street / City / Pincode"
          value={form.customerAddress}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Customer Photo (optional)</label>
        <input
          type="file"
          name="customerPhoto"
          accept="image/*"
          className="field-input"
          onChange={onFileChange}
        />
      </div>
    </section>
  );
}

/* ---- Step 2: KYC details ---- */
function StepKycDetails({ form, onTextChange, onFileChange }) {
  return (
    <section className="section-block">
      <h3 className="section-title">KYC Details</h3>

      <div className="form-group">
        <label className="field-label">Aadhaar Number</label>
        <input
          type="text"
          name="aadharNumber"
          className="field-input"
          placeholder="12-digit Aadhaar number"
          value={form.aadharNumber}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Aadhaar Front Photo</label>
        <input
          type="file"
          name="aadharFront"
          accept="image/*"
          className="field-input"
          onChange={onFileChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Aadhaar Back Photo</label>
        <input
          type="file"
          name="aadharBack"
          accept="image/*"
          className="field-input"
          onChange={onFileChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">PAN Number</label>
        <input
          type="text"
          name="panNumber"
          className="field-input"
          placeholder="ABCDE1234F"
          value={form.panNumber}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">PAN Photo</label>
        <input
          type="file"
          name="panPhoto"
          accept="image/*"
          className="field-input"
          onChange={onFileChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Account Number</label>
        <input
          type="text"
          name="accountNumber"
          className="field-input"
          placeholder="Customer bank account number"
          value={form.accountNumber}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">IFSC Code</label>
        <input
          type="text"
          name="ifscCode"
          className="field-input"
          placeholder="Bank IFSC code"
          value={form.ifscCode}
          onChange={onTextChange}
        />
      </div>
    </section>
  );
}

/* ---- Step 3: Item details ---- */
function StepItemDetails({ form, onTextChange, onFileChange }) {
  return (
    <section className="section-block">
      <h3 className="section-title">Device Details</h3>

      <div className="form-group">
        <label className="field-label">Device Name / Model</label>
        <input
          type="text"
          name="itemName"
          className="field-input"
          placeholder="e.g., iPhone 15, Samsung S24"
          value={form.itemName}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Item Value (₹)</label>
        <input
          type="number"
          name="itemValue"
          className="field-input"
          placeholder="Total device price"
          value={form.itemValue}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">IMEI Number</label>
        <input
          type="text"
          name="itemImei"
          className="field-input"
          placeholder="Device IMEI (optional)"
          value={form.itemImei}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Device Photo (optional)</label>
        <input
          type="file"
          name="itemPhoto"
          accept="image/*"
          className="field-input"
          onChange={onFileChange}
        />
      </div>
    </section>
  );
}

/* ---- Step 4: Downpayment & EMI ---- */
function StepDownpaymentEmi({
  form,
  onTextChange,
  emiOptions,
  selectedPlan,
  setSelectedPlan,
  onDeviceTypeChange,
}) {
  const itemValueNum = Number(form.itemValue) || 0;
  const downPaymentNum = Number(form.downPayment) || 0;

  let minDpText = "";
  if (itemValueNum >= 10000 && itemValueNum <= 20000) {
    minDpText = "Minimum downpayment for this band is 30%";
  } else if (itemValueNum >= 20001 && itemValueNum <= 35000) {
    minDpText = "Minimum downpayment for this band is 30%";
  } else if (itemValueNum >= 35001 && itemValueNum <= 50000) {
    minDpText = "Minimum downpayment for this band is 25%";
  } else if (itemValueNum >= 50001) {
    minDpText = "Minimum downpayment for this band is 20%";
  }

  return (
    <section className="section-block">
      <h3 className="section-title">Downpayment & EMI</h3>

      <div className="form-group">
        <label className="field-label">Item Value (₹)</label>
        <input
          type="number"
          name="itemValue"
          className="field-input"
          placeholder="Total device price"
          value={form.itemValue}
          onChange={onTextChange}
        />
        {minDpText && (
          <p className="text-note" style={{ fontSize: 12, marginTop: 4 }}>
            {minDpText}
          </p>
        )}
      </div>

      <div className="form-group">
        <label className="field-label">Downpayment (₹)</label>
        <input
          type="number"
          name="downPayment"
          className="field-input"
          placeholder="Customer downpayment amount"
          value={form.downPayment}
          onChange={onTextChange}
        />
        {itemValueNum > 0 && downPaymentNum > 0 && (
          <p className="text-note" style={{ fontSize: 12, marginTop: 4 }}>
            Net disbursement: ₹
            {(itemValueNum - downPaymentNum).toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </p>
        )}
      </div>

      <div className="form-group">
        <label className="field-label">CIBIL Score</label>
        <input
          type="number"
          name="cibilScore"
          className="field-input"
          placeholder="Enter CIBIL score"
          value={form.cibilScore}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Current EMI (total)</label>
        <input
          type="number"
          name="currentEmi"
          className="field-input"
          placeholder="All existing EMIs per month"
          value={form.currentEmi}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Device Type</label>
        <div className="button-row" style={{ marginTop: 4 }}>
          <button
            type="button"
            className={
              "btn device-toggle-btn" +
              (form.deviceType === "ANDROID" ? " device-toggle-btn--active" : "")
            }
            onClick={() => onDeviceTypeChange("ANDROID")}
          >
            Android
          </button>
          <button
            type="button"
            className={
              "btn device-toggle-btn" +
              (form.deviceType === "IOS" ? " device-toggle-btn--active" : "")
            }
            onClick={() => onDeviceTypeChange("IOS")}
          >
            iOS
          </button>
        </div>
      </div>

      <div className="section-subtitle" style={{ marginTop: 12 }}>
        Available EMI Options
      </div>

      {emiOptions.length === 0 && (
        <p className="text-note">
          Enter valid Item Value, Downpayment, income, CIBIL, and current EMI to
          see EMI options.
        </p>
      )}

      {emiOptions.length > 0 && (
        <div className="card-list card-list--emi">
          {emiOptions.map((opt) => {
            const isActive = selectedPlan && selectedPlan.key === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                className={
                  "card emi-card emi-card-selectable" +
                  (isActive ? " emi-card--active" : "")
                }
                onClick={() => setSelectedPlan(opt)}
              >
                <div className="card-header-row">
                  <div className="emi-title">
                    {opt.label} • {opt.months} month
                    {opt.months > 1 ? "s" : ""}
                  </div>
                  {isActive && (
                    <span className="badge badge-primary">Selected</span>
                  )}
                </div>

                {/* <div className="emi-row">
                  <span className="emi-label">Loan Amount</span>
                  <span className="emi-value">
                    ₹{opt.loanAmount.toFixed(0)}
                  </span>
                </div> */}

                <div className="emi-row">
                  <span className="emi-label">Last EMI Date</span>
                  <span className="emi-value">
                    {opt.lastRepaymentDate}
                  </span>
                </div>


                <div className="emi-row">
                  <span className="emi-label">Net Disbursement</span>
                  <span className="emi-value">
                    ₹{opt.netDisbursement.toFixed(0)}
                  </span>
                </div>

                <div className="emi-row emi-row--highlight">
                  <span className="emi-label">Monthly EMI</span>
                  <span className="emi-value emi-value--primary">
                    ₹{opt.emi.toFixed(0)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {selectedPlan && (
  <div className="loan-amount-pill">
    ₹
    {selectedPlan.loanAmount.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}
  </div>
)}

    </section>
  );
}

// ---- Step 5 Preview ----
function StepPreview({ retailer, form, selectedPlan, isConfirmed, setIsConfirmed }) {
  const itemValueNum = Number(form.itemValue) || 0;
  const downPaymentNum = Number(form.downPayment) || 0;
  const disbursementAmount =
    selectedPlan?.netDisbursement || itemValueNum - downPaymentNum;

  const adjustedDownPayment = itemValueNum - disbursementAmount;

  const loanAmount = selectedPlan?.loanAmount || 0;
  const avFee = selectedPlan?.avFee ?? 125;
  const otherCharge = selectedPlan?.otherCharge ?? 75;
  const appLockFee = selectedPlan?.appLockFee || 150;

  const totalCharges = loanAmount - disbursementAmount;
  const actualProcessingFee = totalCharges - avFee - appLockFee - otherCharge;

  return (
    <section className="section-block">
      <h3 className="section-title">Preview & Consent</h3>

      {/* Retailer Section */}
      <div className="preview-block">
        <div className="preview-heading">Retailer</div>
        <div className="preview-line">
          <strong>Name:</strong> {retailer?.retailername || "-"}
        </div>
      </div>

      {/* Customer Profile Section */}
      <div className="preview-block">
        <div className="preview-heading">Customer</div>
        <div className="preview-line">
          <strong>Name:</strong> {form.customerName || "-"}
        </div>
        <div className="preview-line">
          <strong>Mobile:</strong> {form.customerPhone || "-"}
        </div>
        {form.customerEmail && (
          <div className="preview-line">
            <strong>Email:</strong> {form.customerEmail}
          </div>
        )}
        <div className="preview-line">
          <strong>DOB:</strong> {form.customerDob || "-"}
        </div>
        <div className="preview-line">
          <strong>Address:</strong> {form.customerAddress || "-"}
        </div>
      </div>

      {/* KYC Information Section */}
      <div className="preview-block">
        <div className="preview-heading">KYC Information</div>
        <div className="preview-line">
          <strong>Aadhar:</strong> {form.aadharNumber || "-"}
        </div>
        <div className="preview-line">
          <strong>PAN:</strong> {form.panNumber || "-"}
        </div>
        <div className="preview-line">
          <strong>Account Number:</strong> {form.accountNumber || "-"}
        </div>
        <div className="preview-line">
          <strong>IFSC Code:</strong> {form.ifscCode || "-"}
        </div>
      </div>

      {/* Device Details Section */}
      <div className="preview-block">
        <div className="preview-heading">Device & Finance</div>
        <div className="preview-line">
          <strong>Device:</strong> {form.itemName || "-"}
        </div>
        <div className="preview-line">
          <strong>Item Value:</strong>{" "}
          ₹
          {itemValueNum.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </div>
        <div className="preview-line">
          <strong>Device Type:</strong> {form.deviceType || "ANDROID"}
        </div>
        {form.itemImei && (
          <div className="preview-line">
            <strong>IMEI:</strong> {form.itemImei}
          </div>
        )}
        <div className="preview-line">
          <strong>Downpayment:</strong>{" "}
          ₹
          {adjustedDownPayment.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </div>
        <div className="preview-line">
          <strong>Net Disbursement:</strong>{" "}
          ₹
          {disbursementAmount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </div>
      </div>

      {/* Selected Plan */}
      {selectedPlan && (
        <div className="preview-block">
          <div className="preview-heading">Selected Plan</div>
          <div className="preview-line">
            <strong>Plan:</strong> {selectedPlan.label} • {selectedPlan.months} month
            {selectedPlan.months > 1 ? "s" : ""}
          </div>
          <div className="preview-line">
            <strong>Loan Amount:</strong>{" "}
            ₹
            {loanAmount.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="preview-line">
            <strong>Monthly EMI:</strong>{" "}
            ₹
            {selectedPlan.emi.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="preview-line">
            <strong>Tenure:</strong> {selectedPlan.months} months
          </div>
          <div className="preview-line">
            <strong>Last Repayment Date:</strong> {selectedPlan.lastRepaymentDate}
          </div>
        </div>
      )}

      {/* Charges Breakdown */}
      {selectedPlan && (
        <div className="preview-block">
          <div className="preview-heading">Processing Fee Breakdown</div>
          <div className="preview-line">
            <strong>AV Management Fee:</strong>{" "}
            ₹
            {avFee.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="preview-line">
            <strong>Insurance:</strong>{" "}
            ₹
            {otherCharge.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="preview-line">
            <strong>App Lock Fee:</strong>{" "}
            ₹
            {appLockFee.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="preview-line">
            <strong>Processing Fee:</strong>{" "}
            ₹
            {actualProcessingFee.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div
            className="preview-line"
            style={{
              borderTop: "2px solid #1e40af",
              paddingTop: "8px",
              marginTop: "8px",
            }}
          >
            <strong style={{ color: "#1e40af" }}>Total Charges:</strong>{" "}
            <strong style={{ color: "#1e40af" }}>
              ₹
              {totalCharges.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </strong>
          </div>
        </div>
      )}

      {/* Confirmation Checkbox */}
      <div className="form-group">
        <label className="field-label">
          <input
            type="checkbox"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
          />{" "}
          I confirm that the above details are correct and I accept the loan
          terms on behalf of the customer.
        </label>
      </div>
    </section>
  );
}

/* Helper: Convert File to base64 data URL */
function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ---- Success view after loan creation ---- */
function SuccessView({ loanId, onClose, retailer, form, selectedPlan }) {
  const { toPDF, targetRef } = usePDF({
    filename: `Credit_Agreement_${loanId}.pdf`,
  });

  const safeForm = form || {};
  const safeRetailer = retailer || {};
  const safePlan = selectedPlan || null;
  const [customerPhotoURL, setCustomerPhotoURL] = React.useState(null);
  const [devicePhotoURL, setDevicePhotoURL] = React.useState(null);

  React.useEffect(() => {
    async function loadImages() {
      if (form.customerPhoto) {
        const url = await fileToDataURL(form.customerPhoto);
        setCustomerPhotoURL(url);
      }
      if (form.itemPhoto) {
        const url = await fileToDataURL(form.itemPhoto);
        setDevicePhotoURL(url);
      }
    }
    loadImages();
  }, [form.customerPhoto, form.itemPhoto]);

  const itemValueNum = Number(safeForm.itemValue) || 0;
  const downPaymentNum = Number(safeForm.downPayment) || 0;

  const disbursementAmount =
    safePlan?.netDisbursement || itemValueNum - downPaymentNum;

  const adjustedDownPayment = itemValueNum - disbursementAmount;

  const COMPANY_NAME = "UTC Finance Pvt. Ltd.";
  const COMPANY_ADDRESS =
    "Plot 47, Sagar Enclave, Rajendra Park, Gurgaon, Sadar Bazar, Gurgaon-122001";
  const COMPANY_PHONE = "+91-7413897932";
  const COMPANY_EMAIL = "hello@usethecred.com";
  const GRIEVANCE_OFFICER = "Sachin Singh";
  const GRIEVANCE_PHONE = "+91-7412813171";

  const avManagementFee = safePlan?.avFee ?? 125;
  const otherCharge = safePlan?.otherCharge ?? 75;  
  const appLockFee = safePlan?.appLockFee ?? 150;

  const loanAmountForAgreement = safePlan?.loanAmount ?? 0;

  // disbursementAmount is already defined just above using safePlan?.netDisbursement
  const totalCharges = Math.round(loanAmountForAgreement - disbursementAmount);

  const actualProcessingFee = Math.max(
    0,
    totalCharges - avManagementFee - appLockFee - otherCharge
  );

  const emiSchedule = [];
if (safePlan) {
  const loanDate = new Date();
  const months = safePlan.months;
  const emiBase = Math.round(safePlan.emi);        // monthly EMI shown everywhere
  const totalRepay = Math.round(safePlan.totalRepay);

  // Build date logic as we discussed earlier (you can plug your 1/10/20-next-month logic here)
  const loanDay = loanDate.getDate();
  let emiDay;
  if (loanDay <= 1) emiDay = 1;
  else if (loanDay <= 10) emiDay = 10;
  else if (loanDay <= 20) emiDay = 20;
  else emiDay = 1;

  const firstEmiDate = new Date(loanDate);
  firstEmiDate.setMonth(firstEmiDate.getMonth() + 1);
  firstEmiDate.setDate(emiDay);
  if (loanDay > 20 && emiDay === 1) {
    firstEmiDate.setMonth(firstEmiDate.getMonth() + 1);
  }

  // Rounding adjustment
  const sumIfAllEqual = emiBase * months;
  const diff = totalRepay - sumIfAllEqual; // can be -1, 0, +1 etc.

  let remainingBalance = totalRepay;

  for (let i = 0; i < months; i++) {
    const emiDate = new Date(firstEmiDate);
    emiDate.setMonth(firstEmiDate.getMonth() + i);

    // Make the LAST EMI absorb the rounding difference
    let emiAmount = emiBase;
    if (i === months - 1) {
      emiAmount = emiBase + diff;
    }

    remainingBalance = Math.max(0, remainingBalance - emiAmount);

    emiSchedule.push({
      emiNumber: i + 1,
      dueDate: emiDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      amount: emiAmount,
      remainingBalance: remainingBalance.toFixed(2),
    });
  }
}




  const handleDownloadPdf = () => {
    toPDF();
  };

  return (
    <>
      {/* Visible success UI */}
      <div className="sheet-body">
        <section className="section-block">
          <h3 className="section-title">Congratulations!</h3>

          <div className="preview-block">
            <div className="preview-heading">
              Credit Agreement Created Successfully
            </div>
            <div className="preview-line">
              Your agreement ID is <strong>{loanId}</strong>.
            </div>
          </div>

          <div className="button-row">
            <button className="btn btn-primary" onClick={handleDownloadPdf}>
              Download Agreement
            </button>
          </div>

          <button
            className="btn btn-secondary"
            style={{ marginTop: 8 }}
            onClick={onClose}
          >
            Close
          </button>
        </section>
      </div>

      {/* Hidden PDF layout */}
      <div
        style={{ position: "absolute", left: "-9999px", top: 0 }}
        ref={targetRef}
      >
        <div className="pdf-root">
          {/* Page 1 */}
          <div className="pdf-page pdf-page--blue">
            <div className="pdf-company-header">
              <img
                src="../public/main_logo.svg"
                alt="UTC Finance"
                className="pdf-company-logo-img"
              />
              <div className="pdf-company-header-info">
                <div className="pdf-company-header-name">
                  UTC Finance Pvt. Ltd.
                </div>
                <div className="pdf-company-header-tagline">
                  Your Trusted Credit Partner
                </div>
              </div>
            </div>

            <div className="pdf-header">
              <div className="pdf-header-left">
                <div className="pdf-doc-title">
                  Mobile Purchase Credit Agreement
                </div>
              </div>
              <div className="pdf-header-right">
                <div className="pdf-header-date">
                  {new Date().toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
                <div className="pdf-header-page">Page 1 of 5</div>
              </div>
            </div>

            <div className="pdf-subheader">
              <div className="pdf-subheader-left">
                <div className="pdf-subheader-label">Agreement ID:</div>
                <div className="pdf-subheader-value">{loanId}</div>
              </div>
            </div>

            <div className="pdf-banner pdf-banner--orange">
              <div className="pdf-banner-title">⚠️ NACH PAYMENT NOTICE</div>
              <div className="pdf-banner-text">
                All EMI payments will be collected via NACH (National Automated
                Clearing House) on the due date. You must maintain sufficient
                balance in your registered bank account to avoid EMI bounce
                charges and penalties. Failure to maintain balance may result in
                late payment charges and device lock.
              </div>
            </div>

            <div className="pdf-grid pdf-grid--3col">
              {/* Customer Photo */}
              <div className="pdf-card">
                <div className="pdf-card-title">Customer Photo</div>
                <div className="pdf-photo-box">
                  {customerPhotoURL ? (
                    <img
                      src={customerPhotoURL}
                      alt="Customer"
                      className="pdf-photo-img"
                    />
                  ) : (
                    <div className="pdf-photo-placeholder">
                      {safeForm.customerName || "Customer"}
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Details */}
              <div className="pdf-card">
                <div className="pdf-card-title">Personal Details</div>
                <table className="pdf-info-table">
                  <tbody>
                    <tr>
                      <td>Name</td>
                      <td>{safeForm.customerName || "-"}</td>
                    </tr>
                    <tr>
                      <td>Mobile</td>
                      <td>{safeForm.customerPhone || "-"}</td>
                    </tr>
                    <tr>
                      <td>Email</td>
                      <td>{safeForm.customerEmail || "-"}</td>
                    </tr>
                    <tr>
                      <td>Date of Birth</td>
                      <td>{safeForm.customerDob || "-"}</td>
                    </tr>
                    <tr>
                      <td>Monthly Income</td>
                      <td>
                        {safeForm.customerMonthlySalary
                          ? Number(safeForm.customerMonthlySalary).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            })
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <td>Address</td>
                      <td>{safeForm.customerAddress || "-"}</td>
                    </tr>
                  </tbody>
                </table>

              </div>

              {/* Device Photo */}
              <div className="pdf-card">
                <div className="pdf-card-title">Device Photo</div>
                <div className="pdf-photo-box">
                  {devicePhotoURL ? (
                    <img
                      src={devicePhotoURL}
                      alt="Device"
                      className="pdf-photo-img"
                    />
                  ) : (
                    <div className="pdf-photo-placeholder">
                      {safeForm.itemName || "Device"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Finance Summary */}
            <div className="pdf-grid pdf-grid--2col">
              <div className="pdf-card">
                <div className="pdf-card-title">Loan Summary</div>
                <table className="pdf-info-table">
                  <tbody>
                    <tr>
                      <td>Retailer</td>
                      <td>{safeRetailer.retailer_name || "-"}</td>
                    </tr>
                    <tr>
                      <td>Device</td>
                      <td>{safeForm.itemName || "-"}</td>
                    </tr>
                    <tr>
                      <td>IMEI Number</td>
                      <td>{safeForm.itemImei || "-"}</td>
                    </tr>
                    <tr>
                      <td>Item Value</td>
                      <td>
                        ₹
                        {itemValueNum.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Downpayment</td>
                      <td>
                        ₹
                        {adjustedDownPayment.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Net Disbursement</td>
                      <td>
                        ₹
                        {disbursementAmount.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    {safePlan && (
                      <>
                        <tr>
                          <td>Loan Amount</td>
                          <td>
                            ₹
                            {loanAmountForAgreement.toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                        </tr>
                        <tr>
                          <td>Monthly EMI</td>
                          <td>
                            ₹
                            {safePlan.emi.toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                        </tr>
                        <tr>
                          <td>Tenure</td>
                          <td>{safePlan.months} months</td>
                        </tr>
                        <tr>
                          <td>Last EMI Date</td>
                          <td>{safePlan.lastRepaymentDate}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Charges Breakdown */}
              <div className="pdf-card">
                <div className="pdf-card-title">Charges Breakdown</div>
                <table className="pdf-info-table">
                  <tbody>
                    <tr>
                      <td>AV Management Fee</td>
                      <td>
                        ₹
                        {avManagementFee.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Insurance</td>
                      <td>
                        ₹
                        {otherCharge.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Device Lock Fee</td>
                      <td>
                        ₹
                        {appLockFee.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Processing Fee</td>
                      <td>
                        ₹
                        {actualProcessingFee.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Total Charges</strong>
                      </td>
                      <td>
                        <strong>
                          ₹
                          {totalCharges.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          })}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Late fee note */}
            <div className="pdf-note">
              <strong>Late Payment Policy:</strong>
              <ul>
                <li>EMIs are due on or before the scheduled due date.</li>
                <li>
                  If EMI is not received on the due date, UTC Finance may lock
                  the device remotely until payment is received.
                </li>
                <li>
                  Late fee may be charged as per company policy and will be
                  communicated by SMS/WhatsApp/email.
                </li>
              </ul>
            </div>
          </div>

          {/* Page 2 - Credit terms & charges */}
    <div className="pdf-page pdf-page--white">
      <div className="pdf-header">
        <div className="pdf-header-left">
          <div className="pdf-doc-title">Mobile Purchase Credit Agreement</div>
        </div>
        <div className="pdf-header-right">
          <div className="pdf-header-date">
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <div className="pdf-header-page">Page 2 of 5</div>
        </div>
      </div>

      <div className="pdf-grid pdf-grid--2col">
        {/* Credit terms */}
        <div className="pdf-card pdf-card--plain">
          <div className="pdf-card-title">Credit Terms</div>
          <table className="pdf-info-table">
            <tbody>
            <tr>
                <td className="pdf-info-label">Device Value</td>
                <td className="pdf-info-value">
                  ₹{itemValueNum.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Downpayment</td>
                <td className="pdf-info-value">
                  ₹{adjustedDownPayment.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Net Disbursement</td>
                <td className="pdf-info-value">
                  ₹{disbursementAmount.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Customer Name</td>
                <td className="pdf-info-value">
                  {safeForm.customerName || "â€”"}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Device Value</td>
                <td className="pdf-info-value">
                  ₹
                  {itemValueNum.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Downpayment</td>
                <td className="pdf-info-value">
                  ₹
                  {downPaymentNum.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Loan Amount</td>
                <td className="pdf-info-value">
                  ₹
                  {safePlan
                    ? safePlan.loanAmount.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })
                    : "â€”"}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Disbursement Amount</td>
                <td className="pdf-info-value">
                  ₹
                  {disbursementAmount.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Tenure</td>
                <td className="pdf-info-value">
                  {safePlan
                    ? `${safePlan.months} months`
                    : "â€”"}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Interest Rate</td>
                <td className="pdf-info-value">
                  {safePlan
                    ? `${safePlan.interestRate?.toFixed(6) || "0.065"}% per day`
                    : "â€”"}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Monthly EMI</td>
                <td className="pdf-info-value pdf-info-value--highlight">
                  ₹
                  {safePlan
                    ? safePlan.emi.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })
                    : "â€”"}
                </td>
              </tr>
              <tr>
                <td className="pdf-info-label">Agreement Date</td>
                <td className="pdf-info-value">
                  {new Date().toLocaleDateString("en-IN")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Charges & fees */}
        <div className="pdf-card pdf-card--plain">
          <div className="pdf-card-title">Charges & Fees Breakdown</div>
          <table className="pdf-info-table">
    <tbody>
      <tr>
        <td className="pdf-info-label">AV Management Fee</td>
        <td className="pdf-info-value">
          ₹{avManagementFee.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </td>
      </tr>
      <tr>
        <td className="pdf-info-label">Insurance</td>
        <td className="pdf-info-value">
          ₹{otherCharge.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </td>
      </tr>
      <tr>
        <td className="pdf-info-label">App Lock Fee</td>
        <td className="pdf-info-value">
          ₹{appLockFee.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </td>
      </tr>
      <tr>
        <td className="pdf-info-label">Processing Fee</td>
        <td className="pdf-info-value">
          ₹{actualProcessingFee.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </td>
      </tr>
      <tr>
        <td className="pdf-info-label">Total Charges</td>
        <td className="pdf-info-value pdf-info-value--highlight">
          ₹{totalCharges.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </td>
      </tr>
    </tbody>
  </table>

          <div className="pdf-banner pdf-banner--yellow" style={{ marginTop: 16 }}>
            <div className="pdf-banner-title">Late Payment Charges</div>
            <div className="pdf-banner-text">
              If payment is delayed beyond the due date:
              <br />
              • One time penalty of 10% of Principle amount will be added.
              <br />
              • 0.066% of Principle Amount will be added as Delay Charge.
            </div>
          </div>

          <div className="pdf-banner pdf-banner--blue" style={{ marginTop: 12 }}>
            <div className="pdf-banner-title">Prepayment Terms</div>
            <div className="pdf-banner-text">
              You may prepay the remaining balance at any time without any
              prepayment penalty or charges. Contact +91-7413897932 for support.
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Page 3 - Monthly payment schedule */}
    <div className="pdf-page pdf-page--white">
      <div className="pdf-header">
        <div className="pdf-header-left">
          <div className="pdf-doc-title">Mobile Purchase Credit Agreement</div>
        </div>
        <div className="pdf-header-right">
          <div className="pdf-header-date">
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <div className="pdf-header-page">Page 3 of 5</div>
        </div>
      </div>

      <div className="pdf-card pdf-card--plain">
        <div className="pdf-card-title">Monthly Payment Schedule</div>

        {emiSchedule.length === 0 ? (
          <div className="pdf-empty-text">No EMI schedule available.</div>
        ) : (
          <table className="pdf-table pdf-table--schedule">
            <thead>
              <tr>
                <th>EMI No.</th>
                <th>Due Date</th>
                <th>Amount (₹)</th>
                <th>Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {emiSchedule.map((row) => (
                <tr key={row.emiNumber}>
                  <td>{row.emiNumber}</td>
                  <td>{row.dueDate}</td>
                  <td>
                    {row.amount.toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })}
                  </td>
                  <td>
                    {Number(row.remainingBalance).toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pdf-banner pdf-banner--yellow" style={{ marginTop: 16 }}>
          <div className="pdf-banner-title">Payment Instructions</div>
          <div className="pdf-banner-text">
            • Payment remainder will be shared through SMS/Whatsapp by official mobile number.
            <br />
            • Always pay on time to avoid bounce charges.
            <br />
            • Recurring EMIs are fixed for every payment.
          </div>
        </div>

        {safePlan && (
          <div className="pdf-total-row">
            <span>Total Amount Payable</span>
            <span>
              ₹
              {safePlan.totalRepay.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}{" "}
              over {safePlan.months} months
            </span>
          </div>
        )}
      </div>
    </div>

    {/* Page 4 - Device lock policy & T&C */}
    <div className="pdf-page pdf-page--white">
      <div className="pdf-header">
        <div className="pdf-header-left">
          <div className="pdf-doc-title">Mobile Purchase Credit Agreement</div>
        </div>
        <div className="pdf-header-right">
          <div className="pdf-header-date">
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <div className="pdf-header-page">Page 4 of 5</div>
        </div>
      </div>

      <div className="pdf-banner pdf-banner--orange">
        <div className="pdf-banner-title">Device Lock & EMI Bounce Policy</div>
        <div className="pdf-banner-text">
          If your EMI payment bounces or is delayed beyond 1 days from the due
          date, UTC Finance Pvt. Ltd. has the right to:
          <br />
          • Lock the device remotely via our app.
          <br />
          • Charge late payment penalties as per the late payment charges
          section.
          <br />
          • Initiate device recovery in case of non-payment.
          <br />
          • If partial payment of atleast 50% id paid, then the grace period will be extended to 15 days.
        </div>
      </div>

      <div className="pdf-card pdf-card--plain">
        <div className="pdf-card-title">Terms & Conditions</div>
        <div className="pdf-terms-text">
          <p>
            1. All EMIs must be paid within 30 days of the due date. UTC Finance
            Pvt. Ltd. has the right to repossess the mobile device without
            further notice if dues are not cleared.
          </p>
          <p>
            2. Device lock will be used responsibly to reduce misuse and fraud.
          </p>
          <p>
            3. The borrower is responsible for providing accurate and complete
            KYC details at the time of loan application.
          </p>
          <p>
            4. Any update in contact details, including mobile number or email
            address, must be shared with UTC Finance Pvt. Ltd. within 7 days.
          </p>
          <p>
            5. In the event of three consecutive EMI bounces, the company may
            initiate recovery proceedings and blacklist the customer in internal
            systems.
          </p>
          <p>
            6. The customer agrees to device locking as a digital collateral
            mechanism.
          </p>
        </div>
      </div>
    </div>

    {/* Page 5 - Acknowledgment & consent */}
    <div className="pdf-page pdf-page--white">
      <div className="pdf-header">
        <div className="pdf-header-left">
          <div className="pdf-doc-title">Mobile Purchase Credit Agreement</div>
        </div>
        <div className="pdf-header-right">
          <div className="pdf-header-date">
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <div className="pdf-header-page">Page 5 of 5</div>
        </div>
      </div>

      <div className="pdf-card pdf-card--plain">
        <div className="pdf-card-title">
          Borrower&apos;s Acknowledgment & Consent
        </div>
        <div className="pdf-terms-text">
          <p>
            I, {safeForm.customerName || "__________"}, hereby acknowledge that
            I have read, understood, and agree to all terms, conditions,
            charges, penalties, and policies outlined in this Mobile Purchase
            Credit Agreement, including:
          </p>
          <ul>
            <li>Monthly EMI amount and schedule.</li>
            <li>All applicable charges and fees including ₹{appLockFee} app lock fee.</li>
            <li>Device lock policy and repossession rules in case of non-payment.</li>
            <li>NACH mandate for automatic EMI deductions from my bank account.</li>
            <li>Use of my KYC data as per applicable RBI and regulatory guidelines.</li>
          </ul>
          <p>
            I consent to receive communication via SMS, WhatsApp, phone calls,
            and email regarding my loan account, EMIs, and payment reminders.
          </p>
        </div>

        <div className="pdf-signature-row">
          <div className="pdf-signature-block">
            <div className="pdf-signature-label">Borrower&apos;s Signature / Initials</div>
            <div className="pdf-signature-line">
              {safeForm.customerName || "____________________"}
            </div>
            <div className="pdf-signature-meta">
              Date: {new Date().toLocaleDateString("en-IN")}
            </div>
          </div>

          <div className="pdf-signature-block">
            <div className="pdf-signature-label">
              Lender&apos;s Representative Signature
            </div>
            <div className="pdf-signature-line">UTC Finance Pvt. Ltd.</div>
            <div className="pdf-signature-meta">
              Date: {new Date().toLocaleDateString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      <div className="pdf-footer-company">
        <div className="pdf-company-name">UTC Finance Pvt. Ltd.</div>
        <div className="pdf-company-details">
          {COMPANY_ADDRESS} • Phone: {COMPANY_PHONE} • Email: {COMPANY_EMAIL}
        </div>
      </div>
    </div>
        </div>
      </div>
    </>
  );
}

export default HomePage;
